import { PDFDocument, PDFPage } from "pdf-lib"
import { pageHeight, pageWidth } from "@services/pdf/constants"
import { IFontSet } from "@services/pdf/fontSet"
import { drawRuns, gray, IRun, ITextStyle, paginateRuns, runsFromText, runsText } from "@services/pdf/textLayout"
import {
	IPdfOptions,
	IPdfPageData,
	showBrand,
	showExhibitions,
	showLiterature,
	showNote,
	showPrice,
	showProvenance,
} from "@services/pdf/types"
import { aspectFitSize, drawImageInTopRect, drawTemplateBackground, drawWhiteBackground, embedImage } from "./shared"
import { taxMessageLiteral } from "@services/pdf/priceTemplate"

// iOS hardcodes these literals in drawWCImageInfoPage
const provenanceHeader = "\nPROVENANCE"
const exhibitionHeader = "\nEXHIBITION HISTORY"
const literatureHeader = "\nLITERATURE"

/**
 * Port of iOS WorkPDFGenerator.drawWCImageInfoPage (White Cube layout).
 * First page: two-column header (artist left / details right) + image below.
 * Notes/provenance/exhibition/literature go on FOLLOW-ON pages only.
 */
export async function drawWCImageInfoPages(
	doc: PDFDocument,
	firstPage: PDFPage,
	item: IPdfPageData,
	options: IPdfOptions,
	price: string,
	templateBytes: Uint8Array | null,
	fonts: IFontSet,
	imageJpeg: Uint8Array | null,
	taxMessage: string
): Promise<void> {
	const width = pageWidth
	const height = pageHeight

	// page spec (iOS)
	const edge = width * 0.066
	const topSectionHeight = height * 0.1
	const imageTopGap = height * 0.052
	const imageBottomGap = height * 0.007
	const maxImageTopOffset = height * 0.06
	const contentArea = { x: edge, yTop: edge, width: width - edge * 2, height: height - edge * 2 }
	const contentMaxX = contentArea.x + contentArea.width
	const contentMaxY = contentArea.yTop + contentArea.height

	// styles
	const detailsFontSize = 8
	const detailsBody: ITextStyle = {
		font: fonts.resolve("BeausiteDetail", "Clear"),
		size: detailsFontSize,
		lineSpacing: 2.5,
		align: "left",
	}
	const detailsItalic: ITextStyle = {
		font: fonts.resolve("BeausiteDetail", "ClearItalic"),
		size: detailsFontSize,
		lineSpacing: 2.5,
		align: "left",
	}
	const extraHeader: ITextStyle = {
		font: fonts.resolve("BeausiteDetail", "Clear"),
		size: 10,
		lineSpacing: 2,
		align: "left",
	}
	const extraBody: ITextStyle = {
		font: fonts.resolve("BeausiteDetail", "Clear"),
		size: 9,
		lineSpacing: 2,
		align: "left",
	}

	// header details (right column) — NOTE: iOS uses item.otherDim as the size line here
	const detailParts: IRun[][] = [
		runsFromText(item.title, detailsItalic),
		runsFromText(item.year, detailsBody),
		runsFromText(item.medium, detailsBody),
		runsFromText(item.otherDim, detailsBody),
		runsFromText(item.edition, detailsBody),
	].filter((runs) => runsText(runs) !== "")

	const headerDetailRuns: IRun[] = []
	detailParts.forEach((part, i) => {
		headerDetailRuns.push(...part)
		if (i < detailParts.length - 1) {
			headerDetailRuns.push({ text: "\n", style: detailsBody })
		}
	})

	if (showPrice(options) && price !== "") {
		if (runsText(headerDetailRuns) !== "") {
			headerDetailRuns.push({ text: "\n\n", style: detailsBody })
		}
		// "plus applicable taxes" suffix rendered italic (iOS range styling)
		const taxIdx = price.indexOf(taxMessage !== "" ? taxMessage : taxMessageLiteral)
		const needle = taxMessage !== "" ? taxMessage : taxMessageLiteral
		if (taxIdx >= 0) {
			headerDetailRuns.push(...runsFromText(price.slice(0, taxIdx), detailsBody))
			headerDetailRuns.push(...runsFromText(needle, detailsItalic))
			headerDetailRuns.push(...runsFromText(price.slice(taxIdx + needle.length), detailsBody))
		} else {
			headerDetailRuns.push(...runsFromText(price, detailsBody))
		}
	}

	// extra info (follow-on pages)
	const extraParts: IRun[][] = [
		runsFromText(showNote(options) ? item.note : "", extraBody),
		runsFromText(showProvenance(options) && item.provenance !== "" ? provenanceHeader : "", extraHeader),
		runsFromText(showProvenance(options) ? item.provenance : "", extraBody),
		runsFromText(showExhibitions(options) && item.exhibition !== "" ? exhibitionHeader : "", extraHeader),
		runsFromText(showExhibitions(options) ? item.exhibition : "", extraBody),
		runsFromText(showLiterature(options) && item.literature !== "" ? literatureHeader : "", extraHeader),
		runsFromText(showLiterature(options) ? item.literature : "", extraBody),
	].filter((runs) => runsText(runs) !== "")

	const extraInfoRuns: IRun[] = []
	extraParts.forEach((part, i) => {
		extraInfoRuns.push(...part)
		if (i < extraParts.length - 1) {
			extraInfoRuns.push({ text: "\n", style: extraBody })
		}
	})

	// ===== first page =====
	drawWhiteBackground(firstPage)
	if (showBrand(options)) {
		await drawTemplateBackground(doc, firstPage, templateBytes)
	}

	// artist (top-left, uppercase, lineSpace 0)
	const headerColumnGap = width * 0.03
	const detailsX = edge + contentArea.width * 0.52
	drawRuns(
		firstPage,
		runsFromText(item.artist.toUpperCase(), {
			font: fonts.resolve("BeausiteText", "Light"),
			size: 14,
			lineSpacing: 0,
			align: "left",
		}),
		{ x: edge, yTop: edge - 2, width: detailsX - edge - headerColumnGap, height: topSectionHeight },
		"top"
	)

	// details + price (top-right)
	drawRuns(
		firstPage,
		headerDetailRuns,
		{ x: detailsX, yTop: edge - 1, width: contentMaxX - detailsX, height: topSectionHeight },
		"top"
	)

	// image
	const imageY = edge + topSectionHeight + imageTopGap
	const imageArea = {
		x: edge,
		yTop: imageY,
		width: contentArea.width,
		height: contentMaxY - imageY - imageBottomGap,
	}

	if (imageJpeg) {
		const image = await embedImage(doc, imageJpeg)
		const scaled = aspectFitSize({ width: image.width, height: image.height }, imageArea)
		const imageTopOffset = Math.min((imageArea.height - scaled.height) / 2, maxImageTopOffset)
		const x = imageArea.x + (imageArea.width - scaled.width) / 2
		drawImageInTopRect(firstPage, image, x, imageArea.yTop + imageTopOffset, scaled.width, scaled.height)
	} else {
		drawRuns(
			firstPage,
			runsFromText("NO IMAGE", {
				font: fonts.resolve("BeausiteDetail", "Clear"),
				size: 11,
				lineSpacing: 4,
				align: "center",
				color: gray,
			}),
			imageArea,
			"center"
		)
	}

	// ===== follow-on pages for extra info =====
	if (runsText(extraInfoRuns) !== "") {
		const extraContentArea = {
			x: contentArea.x,
			yTop: contentArea.yTop + topSectionHeight,
			width: contentArea.width,
			height: contentArea.height - topSectionHeight,
		}

		const emitted: IRun[][] = []
		paginateRuns(
			extraInfoRuns,
			() => ({ width: extraContentArea.width, height: extraContentArea.height }),
			false, // iOS WC loop has no header widow rule
			(pageRuns) => emitted.push(pageRuns)
		)

		for (const pageRuns of emitted) {
			const page = doc.addPage([width, height])
			drawWhiteBackground(page)
			if (showBrand(options)) {
				await drawTemplateBackground(doc, page, templateBytes)
			}
			drawRuns(page, pageRuns, extraContentArea, "top")
		}
	}
}
